import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Zap, Target, Award, TrendingUp, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';

export default function GamificationStatsCard({ profile }) {
  const totalXP = profile?.total_xp || 0;
  const pendingEP = profile?.total_ep_pending_conversion || 0;
  const level = Math.floor(totalXP / 1000) + 1;
  const xpInCurrentLevel = totalXP % 1000;
  const xpProgress = xpInCurrentLevel / 10;

  // Get top skills by SP earned
  const topSkills = (profile?.skills || [])
    .filter(s => s.sp_earned > 0)
    .sort((a, b) => (b.sp_earned || 0) - (a.sp_earned || 0))
    .slice(0, 5);

  const totalSPEarned = (profile?.skills || []).reduce((sum, s) => sum + (s.sp_earned || 0), 0);

  return (
    <Card className="glass-panel border-none relative overflow-hidden">
      <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-amber-500/10 to-transparent rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl" />
      
      <CardHeader>
        <CardTitle className="text-slate-700 font-semibold tracking-tight leading-none flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-amber-400" />
          Gamification Progress
        </CardTitle>
        <p className="text-xs text-slate-500 mt-2">Track your XP, EP, and skill points</p>
      </CardHeader>
      
      <CardContent className="space-y-6">
        {/* Main Stats Row */}
        <div className="grid grid-cols-3 gap-4">
          {/* Total XP */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-br from-amber-500/10 to-amber-500/5 border border-amber-500/20 rounded-xl p-4"
          >
            <div className="flex items-center gap-2 mb-2">
              <Target className="w-4 h-4 text-amber-400" />
              <span className="text-xs text-slate-400 uppercase tracking-wider">Total XP</span>
            </div>
            <div className="text-2xl font-bold text-amber-400 font-mono">{totalXP.toLocaleString()}</div>
            <div className="text-xs text-slate-500 mt-1">Level {level}</div>
          </motion.div>

          {/* Pending EP */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-gradient-to-br from-purple-500/10 to-purple-500/5 border border-purple-500/20 rounded-xl p-4"
          >
            <div className="flex items-center gap-2 mb-2">
              <Zap className="w-4 h-4 text-purple-400" />
              <span className="text-xs text-slate-400 uppercase tracking-wider">Pending EP</span>
            </div>
            <div className="text-2xl font-bold text-purple-400 font-mono">{pendingEP.toLocaleString()}</div>
            <div className="text-xs text-slate-500 mt-1">Awaiting conversion</div>
          </motion.div>

          {/* Total SP */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-gradient-to-br from-emerald-500/10 to-emerald-500/5 border border-emerald-500/20 rounded-xl p-4"
          >
            <div className="flex items-center gap-2 mb-2">
              <Award className="w-4 h-4 text-emerald-400" />
              <span className="text-xs text-slate-400 uppercase tracking-wider">Total SP</span>
            </div>
            <div className="text-2xl font-bold text-emerald-400 font-mono">{totalSPEarned.toLocaleString()}</div>
            <div className="text-xs text-slate-500 mt-1">Skill points earned</div>
          </motion.div>
        </div>

        {/* Level Progress */}
        <div className="space-y-2">
          <div className="flex justify-between text-xs">
            <span className="text-slate-400">Progress to Level {level + 1}</span>
            <span className="text-amber-400 font-mono">{xpInCurrentLevel} / 1000 XP</span>
          </div>
          <Progress value={xpProgress} className="h-2 bg-slate-800" indicatorClassName="bg-gradient-to-r from-amber-500 to-amber-400" />
        </div>

        {/* Top Skills by SP */}
        {topSkills.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-indigo-400" />
              <span className="text-xs text-slate-400 uppercase tracking-wider">Top Skills by SP Earned</span>
            </div>
            <div className="space-y-2">
              {topSkills.map((skill, idx) => (
                <motion.div
                  key={skill.name}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  className="flex items-center justify-between bg-slate-800/30 rounded-lg px-3 py-2"
                >
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs bg-indigo-500/10 text-indigo-400 border-indigo-500/30">
                      Lv.{skill.level || 1}
                    </Badge>
                    <span className="text-sm text-slate-300">{skill.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-emerald-400 font-mono">+{skill.sp_earned || 0} SP</span>
                    {skill.xp > 0 && (
                      <span className="text-xs text-amber-400/60 font-mono">({skill.xp} XP)</span>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {/* EP Explanation */}
        {pendingEP > 0 && (
          <div className="bg-purple-500/5 border border-purple-500/20 rounded-lg p-3">
            <p className="text-xs text-purple-300/80">
              <Zap className="w-3 h-3 inline mr-1" />
              <strong>Pending EP:</strong> Enabler Points from indirect tasks. These convert to XP when the direct tasks they enabled are completed.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}